/** @prettier */
import { addDays, endOfDay, isBefore } from 'date-fns';
import Cookie from 'js-cookie';
import { parse } from 'querystring';
import { LOCATION_CHANGE, push, replace } from 'react-router-redux';
import { delay } from 'redux-saga';

import { PRODUCTS, PRODUCT_LINKS } from '@xanadu/ui-shared/constants';
import { dispatch as gtmDispatch, loginTypes } from '@xanadu/ui-shared/interfaces/googleTagManager';
import {
  userLogin as gtmUserLogin,
  userRegistration as gtmUserRegistration,
} from '@xanadu/ui-shared/interfaces/googleTagManager/actions';
import { track, identify as woopraIdentify } from '@xanadu/ui-shared/interfaces/woopra';
import { setLocalStorageItem } from '@xanadu/ui-shared/modules/dataLayer/actions';
import { SUBMIT_BET } from '@xanadu/ui-shared/modules/inlineBetslip/actions';
import { displayModal } from '@xanadu/ui-shared/modules/modal/actions';
import { resetPasswordModalRequest } from '@xanadu/ui-shared/modules/resetPassword/actions';
import {
  DEPOSIT_SUCCESS,
  dismissMarketingConsentReminder,
  requestBetConfirmationUpdate,
  showResetPassword,
} from '@xanadu/ui-shared/modules/user/actions';
import {
  REGISTRATION_SUCCESS,
  registrationRequest,
} from '@xanadu/ui-shared/modules/userRegistration/actions';
import { initialiseUser, loginRequest } from '@xanadu/ui-shared/modules/userSession/actions';

import { getSignUpFlag } from '../../components/SignUp/utils';

import { updateRightSidebar } from '../rightSidebar/actions';
import { all, fork, put, select, take, takeEvery } from 'redux-saga/effects';

function* watchLocationChanges() {
  while (true) {
    const { payload } = yield take(LOCATION_CHANGE);
    const { pathname, search } = payload;
    const isLoggedIn = yield select((state) => state.getIn(['user', 'isLoggedIn']));
    const product = yield select((state) => state.getIn(['application', 'product'], []));

    if (pathname.indexOf('registration') > -1) {
      if (isLoggedIn) {
        yield put(push('/'));
      } else {
        yield put(
          registrationRequest({ show: true, promoRegistrationImage: null, checked: false })
        );

        track('Registration', 'Registration opened', {
          description: 'url',
          product,
        });
      }
    }

    if (pathname.indexOf('login') > -1) {
      yield put(loginRequest(true));
    }

    if (pathname.indexOf('reset-password') > -1) {
      yield put(showResetPassword(true));
      yield put(loginRequest(true));
    }

    if (pathname.indexOf('update-password') > -1) {
      const urlParams = search ? parse(search.substring(1)) : {};
      yield put(resetPasswordModalRequest(urlParams));

      yield delay(400);
      yield put(push('/'));
    }

    if (pathname.indexOf('/multiples') === 0) {
      yield put(
        updateRightSidebar({
          visible: true,
          content: 'multiples',
        })
      );
      yield delay(400);
      yield put(push('/'));
    }
  }
}

/**
 * Watches for Registration and Deposit success
 * in order to trigger the casino welcome promotion popup
 */
function* handleCasinoWelcomePromotionPopup() {
  while (true) {
    yield take(REGISTRATION_SUCCESS);

    yield take(DEPOSIT_SUCCESS);

    const product = yield select((state) => state.getIn(['application', 'product']));
    const registrationTime = yield select((state) => state.getIn(['user', 'registrationTime']));
    const freeFunds = yield select((state) => state.getIn(['user', 'balance', 'freeFunds']));
    const casinoPromotions = yield select((state) =>
      state.getIn(['promotions', 'main', 'casinoPromotions'])
    );

    const userRegisteredWithin30Days = isBefore(
      new Date(),
      endOfDay(addDays(new Date(registrationTime), 30))
    );

    const firstAvailablePromotion = casinoPromotions.first() || {};

    const casinoProduct = [PRODUCTS.casino, PRODUCTS.liveCasino].includes(product);

    if (!freeFunds && userRegisteredWithin30Days && firstAvailablePromotion.id && casinoProduct) {
      yield delay(1000);

      yield put(
        displayModal({
          show: true,
          section: 'welcome-casino-promotion',
        })
      );
    }
  }
}

function* handleMobileModalBetConfirmationCheck({ options }) {
  const { updateConfirmation } = options;
  if (updateConfirmation) {
    yield requestBetConfirmationUpdate(false);
  }
}

function* watchUserRegistration() {
  while (true) {
    const { payload: accountData } = yield take(REGISTRATION_SUCCESS);

    const product = yield select((state) => state.getIn(['application', 'product']));

    const { url: productHomeLink } =
      PRODUCT_LINKS.find(({ id: productId }) => product === productId) || {};

    yield put(initialiseUser(accountData));

    localStorage.setItem('mb-currency', accountData.currency);
    localStorage.setItem('mb-oddsType', accountData['odds-type-id']);

    yield put(
      setLocalStorageItem({
        key: 'mb-previous-user-login',
        value: {
          accountID: accountData.id,
          userID: accountData['user-id'],
        },
      })
    );

    // Cookie needed for Marketing;
    const userId = accountData.id;
    const legacyUserId = accountData['user-id'];
    Cookie.set('articlevisit', legacyUserId, { expires: 365, domain: '.matchbook.com' });
    gtmDispatch(gtmUserRegistration(accountData));
    gtmDispatch(gtmUserLogin({ loginType: loginTypes.loginButton, data: accountData }));

    woopraIdentify({
      accountId: userId,
    });

    gtmDispatch({
      payload: {
        user_id: userId,
      },
    });

    track('Registration', 'Registration Completed', {
      accountId: userId,
      userId: legacyUserId,
      description: userId,
      trials: getSignUpFlag(),
      visits: getSignUpFlag(),
      product,
    });

    if (!accountData['marketing-consent']) {
      yield put(dismissMarketingConsentReminder());
    }

    if (window.location.pathname.includes('/promo/')) {
      yield put(replace(`${productHomeLink}`));
    }
  }
}

export default function* root() {
  yield all([
    fork(watchLocationChanges),
    fork(handleCasinoWelcomePromotionPopup),
    takeEvery(SUBMIT_BET, handleMobileModalBetConfirmationCheck),
    fork(watchUserRegistration),
  ]);
}
